import { useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useDebounce } from 'use-debounce';
import Button from 'components/Button';
import useWhiteLabelSettings from 'hooks/useWhiteLabelSettings';
import { setIsCardHolderDetailsOpen } from 'reducers/paymentForm/paymentFormSlice';
import { getPaymentDetails } from 'services/CommonService';
import { DEFAULT_PRIMARY_COLOUR } from 'shared/constants/styleConstants';

export default function PaymentForm() {
  const dispatch = useDispatch();
  const { componentConfig, theme, selectedBooking } = useSelector((state) => state.ui);
  const booking = selectedBooking;
  const { reference, currency } = booking;

  const [cardHolderCountries, setcardHolderCountries] = useState('');
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [CardHolderFormError, setCardHolderFormError] = useState('');
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [isLeadGuestDetailsChecked, setIsLeadGuestDetailsChecked] =
    useState(false);
  const { paymentInputValue, selectedPaymentType } = useSelector(
    (state) => state.paymentForm,
  );

  const [formData, setFormData] = useState({
    CardHolderTitle: '',
    CardHolderFirstname: '',
    CardHolderSurname: '',
    CardHolderAddress1: '',
    CardHolderAddress2: '',
    CardHolderCity: '',
    CardHolderState: '',
    CardHolderPostCode: '',
    CardHolderCountryCode: '',
    ContactEmail: '',
  });
  const [debouncedPaymentInputValue] = useDebounce(paymentInputValue, 500);
  const [debouncedFormData] = useDebounce(formData, 500);
  const { whiteLabelSettings: { primaryColour } = {} } =
    useWhiteLabelSettings();

  const toggleLeadGuestDetailsCheckbox = () => {
    setIsLeadGuestDetailsChecked(!isLeadGuestDetailsChecked);

    if (!isLeadGuestDetailsChecked) {
      setFormData({
        CardHolderTitle: paymentDetails.cardHolderTitle,
        CardHolderFirstname: paymentDetails.cardHolderFirstname,
        CardHolderSurname: paymentDetails.cardHolderSurname,
        CardHolderAddress1: paymentDetails.cardHolderAddress1,
        CardHolderAddress2: paymentDetails.cardHolderAddress2,
        CardHolderCity: paymentDetails.cardHolderCity,
        CardHolderState: paymentDetails.cardHolderState,
        CardHolderPostCode: paymentDetails.cardHolderPostCode,
        CardHolderCountryCode: paymentDetails.cardHolderCountryCode,
        ContactEmail: paymentDetails.contactEmail,
      });
    } else {
      setFormData({
        CardHolderTitle: '',
        CardHolderFirstname: '',
        CardHolderSurname: '',
        CardHolderAddress1: '',
        CardHolderAddress2: '',
        CardHolderCity: '',
        CardHolderState: '',
        CardHolderPostCode: '',
        CardHolderCountryCode: '',
        ContactEmail: '',
      });
    }
  };

  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const loadData = useCallback(async () => {
    try {
      const { data: paymentDetailsData } = await getPaymentDetails(reference, selectedPaymentType.code);
      setPaymentDetails(paymentDetailsData);
      setcardHolderCountries(paymentDetailsData.countries || '');
    } catch (error) {
      console.error(error);
    }
  }, [
    reference,
    selectedPaymentType,
  ]);

  useEffect(() => {
    const checkRequiredFields = () => {
      const requiredFields = [
        'CardHolderTitle',
        'CardHolderFirstname',
        'CardHolderSurname',
        'CardHolderAddress1',
        'CardHolderCity',
        'CardHolderPostCode',
        'CardHolderCountryCode',
      ];

      if (componentConfig?.paymentEmailCaptureEnabled) {
        requiredFields.push('ContactEmail');
      }

      const missingFields = requiredFields.filter(
        (key) =>
          !debouncedFormData[key] || debouncedFormData[key].trim() === '',
      );

      if (missingFields.length > 0) {
        setCardHolderFormError('All fields denoted with * are required');
        setIsSubmitDisabled(true);
      } else {
        setCardHolderFormError('');
        setIsSubmitDisabled(false);
      }
    };

    checkRequiredFields();
  }, [
    debouncedPaymentInputValue,
    debouncedFormData,
    paymentDetails,
    currency,
    componentConfig,
  ]);

  useEffect(() => {
    const submitButton = document.getElementById('submitButton');
    if (submitButton) {
      submitButton.disabled = isSubmitDisabled;
    }
  }, [isSubmitDisabled]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div>
      <form
        className="flex flex-wrap justify-between text-left bg-white"
        method="POST"
        action="/make-payment"
      >
        <div className="basis-full xs:basis-1/2 mb-5 pr-4 hidden">
          <label htmlFor="Reference" className="block w-full">
            Reference
            <input
              type="text"
              id="Reference"
              name="Reference"
              className="block w-full border p-2 mt-2"
              placeholder="Reference"
              value={paymentDetails.reference}
              onChange={handleFormInputChange}
            />
          </label>
        </div>
        <div className="basis-full xs:basis-1/2 mb-5 pr-4 hidden">
          <label htmlFor="CurrencyCode" className="block w-full">
            CurrencyCode
            <input
              type="text"
              id="CurrencyCode"
              name="CurrencyCode"
              className="block w-full border p-2 mt-2"
              placeholder="Currency Code"
              value={paymentDetails.currencyCode}
              onChange={handleFormInputChange}
            />
          </label>
        </div>
        {paymentDetails.merchantGuid && (
        <div className="basis-full xs:basis-1/2 mb-5 pr-4 hidden">
          <label htmlFor="merchantGuid" className="block w-full">
            MerchantGuid
            <input
                type="text"
                id="merchantGuid"
                name="merchantGuid"
                className="block w-full border p-2 mt-2"
                placeholder="Merchant Guid"
                value={paymentDetails.merchantGuid}
                onChange={handleFormInputChange}
            />
          </label>
        </div> )}
        <div className="basis-full xs:basis-1/2 mb-5 pr-4 hidden">
          <label htmlFor="DisplayAmount" className="block w-full">
            DisplayAmount
            <input
              type="text"
              id="DisplayAmount"
              name="DisplayAmount"
              className="block w-full border p-2 mt-2"
              placeholder="Postcode"
              value={paymentInputValue}
              onChange={handleFormInputChange}
            />
          </label>
        </div>
        <div className="basis-full md:basis-1/2 mb-5 pr-4 hidden">
          <label htmlFor="Amount" className="block w-full">
            Amount
            <input
              type="text"
              id="Amount"
              name="Amount"
              className="block w-full border p-2 mt-2"
              value={paymentInputValue}
              onChange={handleFormInputChange}
            />
          </label>
        </div>
        <div className="basis-full xs:basis-1/2 mb-5 pr-4 hidden">
          <label htmlFor="CardType" className="block w-full">
            CardType
            <input
              type="text"
              id="CardType"
              name="CardType"
              className="block w-full border p-2 mt-2"
              placeholder="Postcode"
              value={selectedPaymentType.code}
              onChange={handleFormInputChange}
            />
          </label>
        </div>
        <div className="mb-5 basis-full">
          <label htmlFor="LeadGuestDetails" className="block w-full">
            <input
              type="checkbox"
              id="LeadGuestDetails"
              name="LeadGuestDetails"
              className="mr-2"
              checked={isLeadGuestDetailsChecked}
              onChange={toggleLeadGuestDetailsCheckbox}
            />
            Use lead passenger details
          </label>
        </div>
        <div className="basis-full xs:basis-1/2 mb-5 pr-4">
          <label htmlFor="CardHolderTitle" className="block w-full">
            {selectedPaymentType.card ? 'Card Holder Title' : 'Account Holder Title'}
            <span style={{ color: 'red' }}>*</span>
            <input
              type="text"
              id="CardHolderTitle"
              name="CardHolderTitle"
              className="block w-full border p-2 mt-2"
              placeholder="Title"
              value={formData.CardHolderTitle}
              onChange={handleFormInputChange}
            />
          </label>
        </div>
        <div className="basis-full xs:basis-1/2 mb-5 pr-4">
          <label htmlFor="CardHolderFirstname" className="block w-full">
            {selectedPaymentType.card ? 'Card Holder First Name' : 'Account Holder First Name'}
            <span style={{ color: 'red' }}>*</span>
            <input
              type="text"
              id="CardHolderFirstname"
              name="CardHolderFirstname"
              className="block w-full border p-2 mt-2"
              placeholder="First name"
              value={formData.CardHolderFirstname}
              onChange={handleFormInputChange}
            />
          </label>
        </div>
        <div className="basis-full xs:basis-1/2 mb-5 pr-4">
          <label htmlFor="CardHolderSurname" className="block w-full">
            {selectedPaymentType.card ? 'Card Holder Surname' : 'Account Holder Surname'}
            <span style={{ color: 'red' }}>*</span>
            <input
              type="text"
              id="CardHolderSurname"
              name="CardHolderSurname"
              className="block w-full border p-2 mt-2"
              placeholder="Surname"
              value={formData.CardHolderSurname}
              onChange={handleFormInputChange}
            />
          </label>
        </div>
        <div className="basis-full xs:basis-1/2 mb-5 pr-4">
          <label htmlFor="CardHolderAddress1" className="block w-full">
            Address Line 1<span style={{ color: 'red' }}>*</span>
            <input
              type="text"
              id="CardHolderAddress1"
              name="CardHolderAddress1"
              className="block w-full border p-2 mt-2"
              placeholder="Address line 1"
              value={formData.CardHolderAddress1}
              onChange={handleFormInputChange}
            />
          </label>
        </div>
        <div className="basis-full xs:basis-1/2 mb-5 pr-4">
          <label htmlFor="CardHolderAddress2" className="block w-full">
            Address Line 2
            <input
              type="text"
              id="CardHolderAddress2"
              name="CardHolderAddress2"
              className="block w-full border p-2 mt-2"
              placeholder="Address line 2"
              value={formData.CardHolderAddress2}
              onChange={handleFormInputChange}
            />
          </label>
        </div>
        <div className="basis-full xs:basis-1/2 mb-5 pr-4">
          <label htmlFor="CardHolderCity" className="block w-full">
            City<span style={{ color: 'red' }}>*</span>
            <input
              type="text"
              id="CardHolderCity"
              name="CardHolderCity"
              className="block w-full border p-2 mt-2"
              placeholder="City"
              value={formData.CardHolderCity}
              onChange={handleFormInputChange}
            />
          </label>
        </div>
        <div className="basis-full xs:basis-1/2 mb-5 pr-4">
          <label htmlFor="CardHolderState" className="block w-full">
            County
            <input
              type="text"
              id="CardHolderState"
              name="CardHolderState"
              className="block w-full border p-2 mt-2 mt-2"
              placeholder="County"
              value={formData.CardHolderState}
              onChange={handleFormInputChange}
            />
          </label>
        </div>
        <div className="basis-full xs:basis-1/2 mb-5 pr-4">
          <label htmlFor="CardHolderPostCode" className="block w-full">
            Postcode<span style={{ color: 'red' }}>*</span>
            <input
              type="text"
              id="CardHolderPostCode"
              name="CardHolderPostCode"
              className="block w-full border p-2 mt-2"
              placeholder="Postcode"
              value={formData.CardHolderPostCode}
              onChange={handleFormInputChange}
            />
          </label>
        </div>
        <div className="basis-full xs:basis-1/2 mb-5 pr-4">
          <label htmlFor="CardHolderCountryCode" className="block w-full">
            Country<span style={{ color: 'red' }}>*</span>
            <select
              id="CardHolderCountryCode"
              name="CardHolderCountryCode"
              className="block w-full border p-2 mt-2"
              value={formData.CardHolderCountryCode}
              onChange={handleFormInputChange}
            >
              <option value="">Select a country</option>
              {Object.keys(cardHolderCountries).map((countryName) => (
                <option
                  key={countryName}
                  value={cardHolderCountries[countryName]}
                >
                  {countryName}
                </option>
              ))}
            </select>
          </label>
        </div>
        {componentConfig.paymentEmailCaptureEnabled && (
          <div className="basis-full xs:basis-1/2 mb-5 pr-4">
            <label htmlFor="ContactEmail" className="block w-full">
              Email<span style={{ color: 'red' }}>*</span>
              <input
                type="text"
                id="ContactEmail"
                name="ContactEmail"
                className="block w-full border p-2 mt-2"
                placeholder="Email"
                value={formData.ContactEmail}
                onChange={handleFormInputChange}
              />
            </label>
          </div>
        )}
        <div className="flex flex-wrap justify-between basis-full">
          <span className="xs:basis-1/2 text-red-500">
            {CardHolderFormError}
          </span>
        </div>

        <Button
          secondary
          className="mt-2 hidden"
          onClick={() => dispatch(setIsCardHolderDetailsOpen(false))}
        >
          <span>Cancel payment</span>
        </Button>
        <input
          id="submitButton"
          className="mt-2 text-[15px] text-center text-sm rounded-3xl cursor-pointer border-2 font-medium flex justify-center items-center p-4 md:px-8 md:py-2 text-white h-[50px]"
          type="submit"
          value="Continue to payment"
          style={{
            backgroundColor:
              theme.brandColour || primaryColour || DEFAULT_PRIMARY_COLOUR,
          }}
        />
      </form>
    </div>
  );
}
